import {
  RequireIsAnonymous,
  RequireIsLoggedIn,
  RequireNotViewOnly,
} from '@/components/auth'
import RequireHasInterface from '@/components/auth/RequiredHasInterface'
import NotFound from '@/components/common/NotFound'
import OrientationWarning from '@/components/common/OrientationWarning/OrientationWarning'
import PhonePage from '@/components/common/PhonePage'
import VersionCheck from '@/components/common/VersionCheck/VersionCheck'
import { ADMIN_ROUTES } from '@/features/admin/routes'

import NoInterface from '@/features/auth/components/NoInterface'
import { CONFIG_ROUTES } from '@/features/config/routes'
import DesignSystem from '@/features/design-system'
import SetUpContainer from '@/features/design-system/SetUpContainer'
import { CONFIG_SETUP_ROUTES } from '@/features/design-system/routes'
import useOrientationChange from '@/hooks/useOrientationChange'
import { useMediaQuery } from '@mui/material'
import { Fragment, lazy, useEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import { AUTH_ROUTES } from '@/features/auth/routes'
import { EMBED_ROUTES, OPERATIONS_ROUTES } from '@/constant/route'
import CustomSentosaInsightDashboard from '@/features/operations/components/pages/Sensors/CustomSentosaInsightDashboard'
import EmbedPermission from '@/components/auth/EmbedPermission'
import OidcCallback from '@/features/auth/components/OidcCallback'

const AuthContainer = lazy(() => import('@/features/auth/AuthContainer'))

const DashboardContainer = lazy(
  () => import('@/features/admin/DashboardContainer'),
)

const ConfigContainer = lazy(() => import('@/features/config/ConfigContainer'))

const OperationsContainer = lazy(
  () => import('@/features/operations/OperationsContainer'),
)


const rootPath = '/'
// const operationsPath = '/operations'
// const setupPath = '/setup'
const AppRoute = () => {
  const showUIPhone = useMediaQuery('(max-width:767px)')
  const [isPortrait, setIsPortrait] = useState(false)
  const location = useLocation()

  // Use our custom hook to detect orientation
  const { orientation } = useOrientationChange({
    onOrientationChange: (newOrientation) => {
      setIsPortrait(newOrientation === 'portrait')
    },
  })

  // Set initial orientation
  useEffect(() => {
    setIsPortrait(orientation === 'portrait')
  }, [orientation])

  const normalize = (path: string) => path.replace(/^\/+/, '')
  const isEmbedRoute =
    normalize(location.pathname) === normalize(EMBED_ROUTES.index.path)
  if (!isEmbedRoute) {
    // Show phone page for small devices
    if (showUIPhone) return <PhonePage />

    // Show orientation warning for portrait mode on larger devices
    if (isPortrait) return <OrientationWarning />
  }

  return (
    <Fragment>
      <Routes>
        <Route path="/callback" element={<OidcCallback />} />
        <Route
          path={AUTH_ROUTES.index.path}
          element={
            <RequireIsAnonymous>
              <AuthContainer />
            </RequireIsAnonymous>
          }
        />
        <Route
          path={ADMIN_ROUTES.index.path}
          element={
            <RequireIsLoggedIn
              roles={['SuperAdmin', 'Admin', 'Manager', 'User']}
              redirectTo={rootPath}
            >
              <RequireNotViewOnly redirectTo={OPERATIONS_ROUTES.index.to}>
                <DashboardContainer />
              </RequireNotViewOnly>
            </RequireIsLoggedIn>
          }
        />

        <Route
          path={CONFIG_ROUTES.index.path}
          element={
            <RequireIsLoggedIn
              roles={['SuperAdmin', 'Admin', 'Manager', 'User']}
              redirectTo={rootPath}
            >
              <RequireHasInterface
                roles={['Manager', 'User']}
                redirectTo={'/no-interface'}
              >
                <ConfigContainer />
              </RequireHasInterface>
            </RequireIsLoggedIn>
          }
        />

        <Route
          path={OPERATIONS_ROUTES.index.path}
          element={
            <RequireIsLoggedIn
              roles={['SuperAdmin', 'Admin', 'Manager', 'User']}
              redirectTo={rootPath}
            >
              <RequireHasInterface roles={[]} redirectTo={'/no-interface'}>
                <OperationsContainer />
              </RequireHasInterface>
            </RequireIsLoggedIn>
          }
        />

        <Route
          path={CONFIG_SETUP_ROUTES.index.path}
          element={
            <RequireIsLoggedIn
              roles={['SuperAdmin', 'Admin', 'Manager', 'User']}
              redirectTo={rootPath}
            >
              <SetUpContainer />
            </RequireIsLoggedIn>
          }
        />

        <Route path="/design-system" element={<DesignSystem />} />

        <Route
          path={EMBED_ROUTES.index.path}
          element={
            <EmbedPermission>
              <CustomSentosaInsightDashboard />
            </EmbedPermission>
          }
        />

        <Route
          path="/no-interface"
          element={
            <RequireIsLoggedIn
              roles={['SuperAdmin', 'Admin', 'Manager', 'User']}
              redirectTo={rootPath}
            >
              <NoInterface />
            </RequireIsLoggedIn>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>

      <VersionCheck />
    </Fragment>
  )
}

export default AppRoute
